import Tour from '@core/components/Tour';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { produtosTour } from '@core/util/mockTourUtils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { excluirAlert } from 'app/services/alerts';
import { produtoApi } from 'app/services/api/produtoApi';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { ProdutoSearchHeader, ProdutoTable } from './components';
import { CadastroProdutoModal } from './components/modal';
import VisualizarProdutoModal from './components/modal/VisualizarProdutoModal/VisualizarProdutoModal';
import ProdutoBuscaAvancada from './components/ProdutoBuscaAvancada/ProdutoBuscaAvancadaModal';
import { steps } from './components/steps/ProdutoSteps';
import { ProdutoContext, ProdutoProvider } from './ProdutoContext';

const PRODUTO = 'Produto';

const Produto = () => {
  const [, setSelecao] = useContext(ProdutoContext).selecao;
  const [, setTipoSelecao] = useContext(ProdutoContext).tipoSelecao;
  const [, setProdutos] = useContext(ProdutoContext).produtos;
  const [, setProdutoAtual] = useContext(ProdutoContext).produtoAtual;
  const [, setTotalElements] = useContext(ProdutoContext).totalElements;
  const [loading, setLoading] = useContext(ProdutoContext).loading;
  const [search] = useContext(ProdutoContext).search;
  const [sort] = useContext(ProdutoContext).sort;
  const [page, setPage] = useContext(ProdutoContext).page;
  const [size, setSize] = useContext(ProdutoContext).size;
  const [filtroAvancado] = useContext(ProdutoContext).filtroAvancado;
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  const [run, setRun] = useState(false);
  const [onTour, setOnTour] = useState(false);

  const history = useHistory();

  useEffect(() => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    buscarTour();
    // Efeito de montagem: incluir as dependências reexecutaria a carga inicial a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const resetSelecao = () => {
    setSelecao([]);
    setTipoSelecao('NENHUM');
  };

  const initialPage = {
    page,
    q: '',
    size,
    sort: 'nome,ASC',
  };

  const buscarTour = async () => {
    const funcionalidadeJaExistente = funcionalidadesTour?.some((e) => e === 'PRODUTOS');
    if (!funcionalidadeJaExistente) {
      setRun(true);
      setOnTour(true);
      carregarDadosTour();
    } else {
      fetchProdutos();
    }
  };

  const fetchProduto = async (produtoId) => {
    try {
      const response = await produtoApi.getOne(produtoId);
      setProdutoAtual(response.data);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
  };

  const carregarDadosTour = () => {
    setSelecao([]);
    setPage(1);
    setSize(produtosTour.length);
    setTotalElements(3);
    setProdutos(produtosTour);
  };

  const fetchProdutos = async (search = initialPage) => {
    setLoading(true);
    try {
      if (!search?.filtroAvancado) {
        search.filtroAvancado = filtroAvancado;
      }
      const response = await produtoApi.get(search);
      setSelecao([]);
      setPage(response.data.number);
      setSize(response.data.size);
      setTotalElements(response.data.totalElements);
      setProdutos(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
  };

  const handleSelectionChange = (selecao) => {
    setSelecao(selecao);
  };

  const handleUploadImage = async (image) => {
    try {
      const res = await produtoApi.uploadImage(image);

      return { success: true, data: res.data };
    } catch (err) {
      const error = apiUtils.handleApiError(err);
      return { success: false, error };
    }
  };

  const handleSubmit = async (_produto, pageable) => {
    let res;
    let message;

    setLoading(true);

    try {
      if (_produto?.id) {
        eventReactGA(PRODUTO, 'Atualizar produto');
        res = await produtoApi.put(_produto);
        message = 'Produto editado com sucesso.';
      } else {
        eventReactGA(PRODUTO, 'Cadastrar produto');
        res = await produtoApi.post(_produto);
        message = 'Produto salvo com sucesso.';
      }

      showSuccess(message);
      resetSelecao();
      fetchProdutos(pageable);
    } catch (err) {
      apiUtils.handleApiError(err);
    }

    setLoading(false);

    return res;
  };

  const handleDelete = (filtro) => {
    excluirAlert({
      confirmar: async () => {
        try {
          eventReactGA(PRODUTO, 'Excluir produto');
          await produtoApi.delete({ ...filtro, ids: filtro.ids.map((p) => p.id) });
          showSuccess('Produto excluído com sucesso.');
          resetSelecao();
          fetchProdutos();
        } catch (err) {
          apiUtils.handleApiError(err);
        }
      },
    });
  };

  const handleInativar = (filtro) => {
    excluirAlert({
      confirmar: async () => {
        eventReactGA(PRODUTO, 'Inativar produto');
        await produtoApi.inactive({ ...filtro, ids: filtro.ids.map((p) => p.id) });
        fetchProdutos(initialPage);
        showSuccess('Produto inativado com sucesso.');
      },
      opcoes: {
        title: 'Você realmente deseja inativar?',
        text: '',
      },
    });
  };

  const handleAtivar = (filtro) => {
    excluirAlert({
      confirmar: async () => {
        eventReactGA(PRODUTO, 'Ativar produto');
        await produtoApi.active({ ...filtro, ids: filtro.ids.map((p) => p.id) });
        fetchProdutos(initialPage);
        showSuccess('Produto ativado com sucesso.');
      },
      opcoes: {
        title: 'Você realmente deseja ativar?',
        text: '',
      },
    });
  };

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('PRODUTOS');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setOnTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      fetchProdutos({ q: search, size, sort });
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tour
        steps={steps}
        run={run}
        setRun={setRun}
        disableScrolling={true}
        onFinish={() => {
          atualizarTour();
        }}
      />
      <FusePageCarded
        innerScroll
        header={<ProdutoSearchHeader />}
        content={
          <>
            <ProdutoTable
              onSelection={handleSelectionChange}
              fetchProduto={fetchProduto}
              fetchProdutos={fetchProdutos}
              onDelete={handleDelete}
              onInativar={handleInativar}
              onAtivar={handleAtivar}
              onTour={onTour}
            />
            <CadastroProdutoModal onSubmit={handleSubmit} onUploadImage={handleUploadImage} />
            <VisualizarProdutoModal />
            <ProdutoBuscaAvancada fetchProdutos={fetchProdutos} />
          </>
        }
      />
    </>
  );
};

const ProdutoContainer = () => (
  <ProdutoProvider>
    <Produto />
  </ProdutoProvider>
);

export default ProdutoContainer;
